/**
 * Preferencia de orden FORM ↔ OCR cuando hay campos coincidentes (NIF-657).
 * Sin dependencias Angular — puro y testeable.
 */

import { FORM_FIELD_MATCH_OCR_FIELD_IDS } from '../components/modal-workflow-form-step/form-field-presets';
import { resolveMatchOcrFlag } from './workflow-form.util';

/**
 * Campos elegibles para item/modal Autocompletar: mismos que exponen «Cotejar datos».
 * Fuente única: `FORM_FIELD_MATCH_OCR_FIELD_IDS` (incluye address y subcampos).
 */
export const FORM_OCR_COINCIDENT_FIELD_IDS = FORM_FIELD_MATCH_OCR_FIELD_IDS;

const FORM_OCR_COINCIDENT_FIELD_ID_SET = new Set<string>(FORM_OCR_COINCIDENT_FIELD_IDS);

export type FormOcrOrderChoice = 'document' | 'manual';

/** Lee id canónico del campo (editor o API). */
export function resolveFormFieldCanonicalId(field: Record<string, unknown>): string {
	return String(field['id'] ?? field['name'] ?? '');
}

/** Aplana `fields[]` de pasos FORM (raw value o API). */
export function flattenFormStepFields(
	formSteps: ReadonlyArray<Record<string, unknown>> | null | undefined,
): Record<string, unknown>[] {
	if (!formSteps?.length) {
		return [];
	}
	return formSteps.flatMap((step) => {
		const fields = step['fields'];
		return Array.isArray(fields) ? (fields as Record<string, unknown>[]) : [];
	});
}

/** Hay al menos un campo con opción «Cotejar datos» (match_ocr eligible). */
export function hasCoincidentFormOcrFields(
	formSteps: ReadonlyArray<Record<string, unknown>> | null | undefined,
): boolean {
	return flattenFormStepFields(formSteps).some((field) =>
		FORM_OCR_COINCIDENT_FIELD_ID_SET.has(resolveFormFieldCanonicalId(field)),
	);
}

/** Algún campo elegible tiene cotejo `match_ocr` activo → oculta item/modal. */
export function hasCoincidentFieldWithMatchOcr(
	formSteps: ReadonlyArray<Record<string, unknown>> | null | undefined,
): boolean {
	return flattenFormStepFields(formSteps).some((field) => {
		const id = resolveFormFieldCanonicalId(field);
		if (!FORM_OCR_COINCIDENT_FIELD_ID_SET.has(id)) {
			return false;
		}
		return resolveMatchOcrFlag(field);
	});
}

/**
 * Preferencia `autofill_from_ocr` tras guardar/borrar pasos FORM (NIF-657).
 * Sin campos coincidentes → `null` (sin item/modal; evita flag residual).
 * Con coincidentes: misma regla que `resolveAutofillAfterCotejoChange`.
 */
export function resolveAutofillAfterFormStepsChange(input: {
	hasCoincidentFields: boolean;
	hadCotejo: boolean;
	hasCotejo: boolean;
	currentAutofill: boolean | null | undefined;
}): boolean | null {
	if (!input.hasCoincidentFields) {
		return null;
	}
	return resolveAutofillAfterCotejoChange({
		hadCotejo: input.hadCotejo,
		hasCotejo: input.hasCotejo,
		currentAutofill: input.currentAutofill,
	});
}

/**
 * Preferencia `autofill_from_ocr` tras guardar pasos FORM (NIF-657 / comment 124478).
 * Con cotejo → `false`. Si se quita cotejo → `null` (reabrir modal). Si no hay transición, keep.
 */
export function resolveAutofillAfterCotejoChange(input: {
	hadCotejo: boolean;
	hasCotejo: boolean;
	currentAutofill: boolean | null | undefined;
}): boolean | null {
	if (input.hasCotejo) {
		return false;
	}
	if (input.hadCotejo && !input.hasCotejo) {
		return null;
	}
	return input.currentAutofill ?? null;
}

/**
 * ¿Mostrar modal al Continuar en Formulario (hacia OCR)?
 * FORM+OCR, coincidentes, sin cotejo, preferencia aún no elegida (`null`).
 */
export function shouldPromptFormOcrOrder(input: {
	formAvailable: boolean;
	ocrAvailable: boolean;
	formSteps: ReadonlyArray<Record<string, unknown>> | null | undefined;
	autofillFromOcr: boolean | null | undefined;
}): boolean {
	if (!input.formAvailable || !input.ocrAvailable) {
		return false;
	}
	if (!hasCoincidentFormOcrFields(input.formSteps)) {
		return false;
	}
	if (hasCoincidentFieldWithMatchOcr(input.formSteps)) {
		return false;
	}
	return input.autofillFromOcr == null;
}

/**
 * ¿Mostrar item resumen en panel FORM?
 * FORM+OCR + coincidentes **sin cotejo** (aunque preferencia sea `null`).
 * Con cotejo: sin item ni modal — Formulario→OCR (`autofill_from_ocr: false`; Carmen).
 */
export function shouldShowFormOcrOrderSummary(input: {
	formAvailable: boolean;
	ocrAvailable: boolean;
	formSteps: ReadonlyArray<Record<string, unknown>> | null | undefined;
}): boolean {
	if (!input.formAvailable || !input.ocrAvailable) {
		return false;
	}
	if (!hasCoincidentFormOcrFields(input.formSteps)) {
		return false;
	}
	if (hasCoincidentFieldWithMatchOcr(input.formSteps)) {
		return false;
	}
	return true;
}

/**
 * UI del item: Autocompletar por defecto si aún no hay elección (`null`).
 * Solo Manual cuando la preferencia es explícitamente `false`.
 */
export function isFormOcrOrderDocumentUi(autofillFromOcr: boolean | null | undefined): boolean {
	return autofillFromOcr !== false;
}

/** Tag ACTIVO solo con preferencia confirmada Autocompletar. */
export function isFormOcrOrderAutofillConfirmed(autofillFromOcr: boolean | null | undefined): boolean {
	return autofillFromOcr === true;
}

/** Mapea choice UI ↔ flag API. */
export function formOcrOrderChoiceToAutofill(choice: FormOcrOrderChoice): boolean {
	return choice === 'document';
}

export function autofillToFormOcrOrderChoice(autofillFromOcr: boolean | null | undefined): FormOcrOrderChoice | null {
	if (autofillFromOcr === true) {
		return 'document';
	}
	if (autofillFromOcr === false) {
		return 'manual';
	}
	return null;
}
